import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { DataService } from './data.service';
import { PlayerState } from './shared/enums/player-states';
import { DialogService } from './shared/services/dialog.service';
import { NavigationLibraryService } from './shared/services/navigation/navigation.service';
import { SimplePlayerService } from './shared/shared-components/simple-player/simple-player.service';

import { takeUntil } from 'rxjs/operators';
import { AppEventName } from './analytics/newrelic/enums/EventName';
import { NewrelicService } from './analytics/newrelic/services/newrelic.service';
import { CollectionComponent } from './collection/collection.component';
import { DetailPageComponent } from './detail-page/detail-page.component';
import { DetailPageModeService } from './detail-page/services/detail-mode.service';
import { LanguageService } from './language.service';
import { PlayerService } from './player/player.service';
import { Destroyable } from './shared/services/destroyable';
import { RouteHistoryService } from './shared/services/router-history.service';
import { StorefrontComponent } from './storefront/storefront/storefront.component';
import { CollectionStorefrontComponent } from './storefront/collection-storefront/collection-storefront.component';
import { TizenService } from './shared/services/tizen/tizen.service';
import { KEYS } from './shared/utils/constants/storage.constants';
import { SessionStorageService } from './shared/services/storage/session-storage.service';
import { getBrowserDetector, deepLink } from './shared/utils/browserUtils';
import { AuthService } from './shared/services/auth/auth.service';
import { RefreshService } from './shared/services/evergent/login/refresh.service';
import { ConfigService } from './shared/services/config.service';
import { EntitlementService } from './shared/services/evergent/entitlement/entitlement.service';
import { LocalStorageService } from './shared/services/storage/local-storage.service';
import { setExpirationTime } from './shared/utils/LoginUtils';
import { SignOutService } from './shared/shared-components/exit-app-and-sign-out/sign-out/sign-out.service';

declare const tizen: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent extends Destroyable implements OnInit, OnDestroy {
  title = 'aha-smarttv';
  isVideoPlaying = false;
  videoError = false;
  showSimplePlayer = true;
  isLoading = true;
  isNoInternet = false;
  private _component: any;

  // Event handlers
  private _windowOnlineHandler: () => void;
  private _windowOfflineHandler: () => void;
  private _documentVisibilityChange: () => void;
  private _windowBlurHandler: () => void;
  private _windowFocusHandler: () => void;
  private _previewDestroyed = false;
  private _onlineTimer: any;

  constructor(
    private readonly navService: NavigationLibraryService,
    private readonly dataService: DataService,
    private readonly simplePlayerService: SimplePlayerService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly languageService: LanguageService,
    private readonly routeHistoryService: RouteHistoryService,
    private readonly dialogService: DialogService,
    private readonly playerService: PlayerService,
    private readonly newrelic: NewrelicService,
    private readonly detailPageModeService: DetailPageModeService,
    private readonly tizenService: TizenService,
    private readonly sessionStorage: SessionStorageService,
    private readonly authService: AuthService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly refreshService: RefreshService,
    private readonly configService: ConfigService,
    private readonly entitleService: EntitlementService,
    private readonly localStorage: LocalStorageService,
    private readonly signOutService: SignOutService
  ) {
    super();
    // Add class to body if Chromium 37 is detected
    if (/Chrome\/37\./.test(navigator.userAgent)) {
      this.document.body.classList.add('disable-animations');
    }
    this.routeHistoryService.init();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const canShowSimplePlayer = this.shouldShowSimplePlayer();
        this.showSimplePlayer = canShowSimplePlayer;
        if (!canShowSimplePlayer) {
          this.simplePlayerService.resetEvent();
        } else {
          const resource = this.dataService.currentResourceValue;
          if (resource) {
            this.simplePlayerService.handleResource(resource);
          }
        }
      });
    this.handleStartupLogic();
  }

  ngOnInit() {
    this.setResolution();
    this.initEventHandlers();
    this.addEventListeners();
    this.languageService.setDefault();
    this.navService.init();
    this.dataService.currentResource
      .pipe(takeUntil(this.destroy$))
      .subscribe((resource) => {
        if (resource && this.showSimplePlayer) {
          this.simplePlayerService.handleResource(resource);
        }
      });

    this.dialogService.dialogStateChanges$
      .pipe(
        filter(() => this.showSimplePlayer),
        takeUntil(this.destroy$)
      )
      .subscribe((hasOpenDialog) => {
        if (this._component === DetailPageComponent) {
          const playerState = hasOpenDialog
            ? PlayerState.PAUSE
            : PlayerState.RESUME;
          this.detailPageModeService.setPlayerState(playerState);
        } else {
          this.simplePlayerService.setDialogState(hasOpenDialog);
        }
      });
    if (!this.dataService.isTizenPreviewShown) {
      this.tizenService.checkOperation();
    }
  }

  ngOnDestroy() {
    this.cleanup();
    this.removeEventListeners();
    if (this._onlineTimer) {
      clearTimeout(this._onlineTimer);
    }
  }

  private handleAppBackground(managePreview = true) {
    this.setPlayerState(true, { managePreview, appState: 'background' });
  }

  private async handleAppForeground(
    managePreview = true,
    updateSimplePlayer = false,
    networkState: 'online' | 'offline' | null = null
  ) {
    /* this.playerService.updatePlayerState({
      playerState: PlayerState.RESUME,
      networkState: null,
      appState: 'foreground',
    }); */
    if (!this.isNoInternet) {
      this.authService.resetPlayback();
      await this.authService.initPlatformService();
      await this.authService.contentAuthorizer.registerDevice();
      if (this.authService.isLoggedIn()) {
        try {
          await this.refreshService.makeRefreshTokenCall(
            this.configService.applicationConfig
          );
        } catch (error) {
          console.error('Error refreshing token on foreground:', error);
          this.signOutService.forceLogout();
          return;
        }
      }
    }
    if (this.dialogService.hasOpenDialog()) {
      this.navService.focusContainer('generic-dialog', true, 0);
    } else if (this.isNoInternet) {
      this.navService.setIndexContainer('container-right', 0);
      this.navService.focusContainer('container-right', true, 0);
      this.setPlayerState(false, {
        managePreview,
        updateSimplePlayer,
        networkState,
        appState: 'foreground',
      });
    } else {
      if (networkState === 'online') {
        this.dataService.emitVerticalMenuEvent({ action: 'close' });
        this.navService.focus(200);
      }

      this.setPlayerState(false, {
        managePreview,
        updateSimplePlayer,
        networkState,
        appState: 'foreground',
      });
    }
  }

  private initEventHandlers() {
    this._windowOnlineHandler = () => {
      if (this._onlineTimer) {
        clearTimeout(this._onlineTimer);
      }
      this._onlineTimer = setTimeout(() => {
        this.isNoInternet = false;
        this.handleAppForeground(false, true, 'online');
      }, 2000);
    };
    this._windowOfflineHandler = () => {
      if (this._onlineTimer) {
        clearTimeout(this._onlineTimer);
      }
      this.isNoInternet = true;
      this.setPlayerState(true, {
        updateSimplePlayer: true,
        networkState: 'offline',
      });
    };
    this._documentVisibilityChange = () => {
      if (this.document.visibilityState === 'hidden') {
        this._previewDestroyed = true;
        this.handleAppBackground(true);
        this.newrelic.sendAppEvent(AppEventName.APP_BACKGROUND);
      } else {
        this._previewDestroyed = false;
        this.handleAppForeground(true);
        this.newrelic.sendAppEvent(AppEventName.APP_FOREGROUND);
      }
    };
    // LG webOS screen saver fires blur/focus instead of visibilitychange.
    // We only manage the simple player preview here — NOT the full player —
    // so that opening the TV settings panel during playback (which also fires
    // blur) does not stop the video. The showSimplePlayer guard ensures we
    // ignore blur when the full player route is active.
    this._windowBlurHandler = () => {
      if (this.showSimplePlayer && !this._previewDestroyed) {
        this._previewDestroyed = true;
        this.simplePlayerService.forceDestroyPreview();
      }
    };
    this._windowFocusHandler = () => {
      if (this._previewDestroyed) {
        this._previewDestroyed = false;
        this.simplePlayerService.forceRestorePreview();
      }
    };
    window.addEventListener('resize', () => this.setResolution());
  }

  private addEventListeners() {
    this.document.addEventListener(
      'visibilitychange',
      this._documentVisibilityChange
    );
    window.addEventListener('blur', this._windowBlurHandler);
    window.addEventListener('focus', this._windowFocusHandler);
    window.addEventListener('online', this._windowOnlineHandler, true);
    window.addEventListener('offline', this._windowOfflineHandler, true);
  }

  private removeEventListeners() {
    this.document.removeEventListener(
      'visibilitychange',
      this._documentVisibilityChange
    );
    window.removeEventListener('blur', this._windowBlurHandler);
    window.removeEventListener('focus', this._windowFocusHandler);
    window.removeEventListener('online', this._windowOnlineHandler, true);
    window.removeEventListener('offline', this._windowOfflineHandler, true);
  }

  private setPlayerState(
    isPause: boolean,
    options: {
      updateSimplePlayer?: boolean;
      managePreview?: boolean;
      networkState?: 'online' | 'offline' | null;
      appState?: 'foreground' | 'background';
    } = {}
  ) {
    const state = isPause ? PlayerState.PAUSE : PlayerState.RESUME;
    try {
      this.playerService.updatePlayerState({
        playerState: state,
        networkState: options.networkState,
        appState: options.appState,
      });
    } catch (error) {
      console.error('Error updating player state:', error);
    }
    if (options.updateSimplePlayer) {
      this.simplePlayerService.updatePlayerState(state);
    }
    if (options.managePreview) {
      if (isPause) {
        this.simplePlayerService.forceDestroyPreview();
      } else {
        this.simplePlayerService.forceRestorePreview();
      }
    }
  }

  private shouldShowSimplePlayer(): boolean {
    let route = this.route.snapshot;
    // Traverse to the deepest child
    while (route.firstChild) {
      route = route.firstChild;
    }
    this._component = route.routeConfig?.component;
    this.dataService.setCurrentComponent = this._component;
    return (
      this._component === StorefrontComponent ||
      this._component === DetailPageComponent ||
      this._component === CollectionComponent ||
      this._component === CollectionStorefrontComponent
    );
  }

  private handleStartupLogic() {
    try {
      // 1. (Tizen initialization is now handled in DeeplinkGuard)
      /*  this.tizenService.handleRedirect({
        catalogLanguage: 'ta',
        contentType: 'movie',
        resourceId: '5D809431-24C0-41DF-A705-DF99B1E6F993',
        isPlayable: true,
      }); */
      // 2. Send APP_START event if entering via deeplink
      this.sendAppStartForDeeplink();
    } catch (error) {
      console.error('Error in handleStartupLogic:', error);
    }
  }

  private sendAppStartForDeeplink(): void {
    // Guard: only send once per session
    if (this.dataService.isAppStartSent) {
      return;
    }

    // Detect deeplink: browser deeplinks are captured by AppLoadService;
    // Tizen deeplinks need payload inspection since the URL is always '/'
    const hasBrowserDeeplink = !!this.dataService.getPendingDeeplinkUrl;
    const hasTizenDeeplink = getBrowserDetector().isTizen() && !!deepLink();
    const isDeeplink = hasBrowserDeeplink || hasTizenDeeplink;

    if (!isDeeplink) {
      return;
    }

    const elapsedSplash = this.sessionStorage.getItem(KEYS.ELAPSED_SPLASH);
    const timeToAppStart = elapsedSplash
      ? Date.now() - Number(elapsedSplash)
      : 0;
    this.newrelic.sendAppEvent(AppEventName.APP_START, {
      timeToAppStart,
    });

    // Mark as sent so storefront and other components skip their own send
    this.dataService.isAppStartSent = true;
  }
  private setResolution() {
    this.dataService.resolution = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }
}
